import { useState, useEffect, useMemo } from "react";
import { FormikValues, useFormik } from "formik";
import { Button } from "@almafintech/react-components/Button";
import "../../App.css";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  onUpdateStep,
  onUpdateFormData,
} from "../../reducers/onboardingDataReducer";
import { onUpdateFormData as onUpdateSecuritiesFormData } from "../../reducers/securitiesReducer";
import { CompanyType, Field, Step } from "../../types";
import FormContainer from "../FormContainer/FormContainer";
import Container from "../Container/Container";
import Layout from "../Layout/Layout";
import { useNavigate, useSearchParams } from "react-router-dom";
import TemplateInterpreterForm from "./TemplateInterpreterForm";
import TemplateInterpreterSecondaryForm from "./TemplateInterpreterSecondaryForm";
import {
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
  onUpdateTertiaryButton,
} from "../../reducers/buttonsReducer";
import {
  isBasicStep,
  isComponentStep,
  isJuridicalOnboarding,
  isUnifiedOnboarding,
  loadFavicon,
} from "../../utils";
import { LoadingCircle } from "@almafintech/react-components/LoadingCircle";
import PEPModal from "../PEPModal/PEPModal";
import TramitNumberModal from "../TramitNumberModal/TramitNumberModal";
import RiskProfileModal from "../RiskProfileModal/RiskProfileModal";
import RiskProfileModalSecurities from "../RiskProfileModalSecurities/RiskProfileModal";
import { onUpdateInfoStep } from "../../reducers/infoStepReducer";
import { getDraft } from "../../connectors/connectors";
import {
  generateYupSchema,
  getKeys,
  getOptionalFieldNames,
  getRequiredFieldNames,
  getValue,
  isEmptyObject,
  reorderFields,
  setNestedValue,
  validPathsPerCompany,
} from "./utils";
import useForm from "../../hooks/useForm";
import { logAnalyticsAndHotjarEvent } from "../../main";
import EditDNIModal from "../EditDNIModal/EditDNIModal";
import AccountObjectiveModal from "../AccountObjectiveModal/AccountObjectiveModal";
import {
  primaryButtonText,
  secondaryButtonText,
  tertiaryButtonText,
} from "../../i18n/interpreter";
import SupplementaryComponentInfoRenderer from "../SupplementaryComponentInfoRenderer/SupplementaryComponentInfoRenderer";
import { isDev } from "../../app/utils";
import useTemplateInterpreter from "../../hooks/useTemplateInterpreter";

import styles from "./TemplateInterpreter.module.scss";

const TemplateInterpreter = () => {
  const {
    loading: loadingStyle,
    buttonsGroup,
    button,
    buttonTertiary,
    headerFormContainer,
    component,
    secondaryFormTemplate,
    templateContainer,
    form,
    flexColumn,
  } = styles;

  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState({
    template: true,
    formValues: true,
  });

  const state = useAppSelector((state) => state);

  const {
    activeTemplate: template,
    buttons: { primaryButtonData, secondaryButtonData, tertiaryButtonData },
    onboardingData: {
      stepData: { step, subStep, reachedSteps },
    },
    preconfirmation: { isEditingForm },
    securities,
  } = state;

  // Email or name of the person that referred the user
  const referrer = searchParams.get("ref");
  // Get the email from the URL
  const emailByUrl = searchParams.get("accountEmail")?.replace(/ /g, "+"); // Prevent spaces in email from breaking the URL
  const cuitByUrl = searchParams.get("accountCuit");

  // Redirect away from paths that shouldn't be reached given the set VITE_COMPANY
  useEffect(() => {
    const company = import.meta.env.VITE_COMPANY as CompanyType;
    const { pathname } = window.location;
    if (!validPathsPerCompany[company].includes(pathname))
      navigate(validPathsPerCompany[company]?.[0] ?? pathname);
  }, []);

  useEffect(() => {
    if (template.onboardingType) return;
    redirectToOnboarding();
  }, [template]);

  // Load template
  // Set favicon
  useEffect(() => {
    if (!template?.onboardingType) return;

    if (!template.formTitle) {
      loadTemplate();
    } else {
      setLoading((prevState) => ({
        ...prevState,
        template: false,
      }));
    }

    if (template?.favicon) {
      loadFavicon(template.favicon);
    }
  }, [template]);

  useEffect(() => {
    if (!!template?.onboardingType && state.securities.language) {
      loadTemplate();
    }
  }, [
    state.securities.language,
    state.securities.accountBeneficiaries,
    state.securities.beneficiariesCount,
    state.securities.currentHolder,
    state.securities.accountHolders,
    state.securities.draftUUID,
    state.securities.holdersData,
    state.securities.formValues,
    template.onboardingType,
  ]);

  // Flatten step fields into a single array
  const flattenStepFields = useMemo(
    () =>
      template?.steps?.reduce(
        (acc: Field[], { fields, subSteps, secondaryForm }) => {
          if (subSteps) {
            subSteps.forEach(({ fields, secondaryForm }: Step) => {
              if (fields && !secondaryForm) acc.push(...fields);
            });
          } else {
            if (fields && !secondaryForm) acc.push(...fields);
          }
          return acc;
        },
        []
      ),
    [template?.steps]
  );

  // Flatten step fields initial values
  const flattenStepFieldsInitialValues = useMemo(
    () =>
      flattenStepFields?.reduce((acc: FormikValues, { name, initialValue }) => {
        setNestedValue(acc, name, initialValue);
        return acc;
      }, {}),
    [flattenStepFields]
  );

  // Generate Yup schema for form validation
  const flattenStepFieldsSchema = generateYupSchema(flattenStepFields, state);

  // Set initial values for formik
  const initialValues = flattenStepFieldsInitialValues;

  // Formik
  const formik = useFormik({
    initialValues: initialValues || {},
    validationSchema: flattenStepFieldsSchema,
    onSubmit: (_, { setSubmitting }) => {
      setSubmitting(false);
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    isSubmitting,
    setValues,
    resetForm,
  } = formik;

  const { currentStep, currentStepData, handleBack, handleContinue } =
    useForm(formik);

  const {
    loadTemplate,
    formValues,
    draftUUID,
    redirectToOnboarding,
    language,
    shouldCheckForDraft,
  } = useTemplateInterpreter({
    dispatch,
    onboardingType: template.onboardingType,
    values,
    state,
  });

  // Set initial form values dynamically
  useEffect(() => {
    if (
      (flattenStepFieldsInitialValues && !formValues) ||
      (formValues && isEmptyObject(formValues))
    ) {
      flattenStepFieldsInitialValues &&
        dispatch(
          template?.onboardingType === "SECURITIES"
            ? onUpdateSecuritiesFormData(flattenStepFieldsInitialValues)
            : onUpdateFormData(flattenStepFieldsInitialValues)
        );
    }
  }, [flattenStepFieldsInitialValues, dispatch, formValues]);

  const [initialFormDataLoaded, setInitialFormDataLoaded] = useState(false);

  // Check draft availability
  useEffect(() => {
    const checkDraftAvailability = async () => {
      try {
        await getDraft({ draftUUID });
        return true;
      } catch (error) {
        return false;
      }
    };

    const checkDraft = async () => {
      if (
        !initialFormDataLoaded &&
        loading?.formValues &&
        formValues &&
        !isEmptyObject(formValues)
      ) {
        let formValuesDefault = formValues;
        if (!referrer && formValuesDefault?.referrer && !reachedSteps) {
          formValuesDefault = {
            ...formValuesDefault,
            referrer: "",
            referredBool: "",
          };
        }

        setValues((prevValues) => {
          return {
            ...prevValues,
            ...formValuesDefault,
          };
        }, false);

        setLoading((prevState) => ({
          ...prevState,
          formValues: false,
        }));

        setInitialFormDataLoaded(true);

        if (
          reachedSteps &&
          reachedSteps.find((step) => step > 2) &&
          !isEditingForm
        ) {
          try {
            const draftAvailable = await checkDraftAvailability();

            if (draftAvailable) {
              dispatch(onUpdateInfoStep("PENDING_REGISTRY_LOCAL"));
              dispatch(onUpdateStep("information-step"));
            } else {
              resetForm();
              dispatch({ type: "reset-store" });
              dispatch(onUpdateStep(1));
            }
          } catch {
            resetForm();
            dispatch({ type: "reset-store" });
            dispatch(onUpdateStep(1));
          }
        } else if (reachedSteps && !reachedSteps.find((step) => step === 3)) {
          resetForm();
          dispatch({ type: "reset-store" });
          dispatch(onUpdateStep(1));
        }
      }
    };

    if (shouldCheckForDraft) checkDraft();
  }, [loading.formValues, formValues, initialFormDataLoaded]);

  useEffect(() => {
    if (
      !shouldCheckForDraft &&
      loading?.formValues &&
      formValues &&
      !isEmptyObject(formValues)
    ) {
      setValues(
        (prevValues) => ({
          ...prevValues,
          ...formValues,
        }),
        false
      );
      setLoading((prevState) => ({
        ...prevState,
        formValues: false,
      }));
    }
  }, [template.onboardingType, formValues, loading.formValues]);

  // Get required fields names from current step
  const currentStepRequiredFieldsNames = useMemo(() => {
    if (!isBasicStep(currentStepData)) return [];

    if ("fields" in currentStepData && currentStepData.fields) {
      return getRequiredFieldNames(
        currentStepData.fields,
        state,
        values,
        dispatch
      );
    } else {
      return [];
    }
  }, [currentStepData, values]);

  const currentStepOptionalFieldsName = useMemo(() => {
    if (!isBasicStep(currentStepData)) return [];

    if ("fields" in currentStepData && currentStepData.fields) {
      return getOptionalFieldNames(
        currentStepData.fields,
        state,
        values,
        dispatch
      );
    } else {
      return [];
    }
  }, [currentStepData, values]);

  const primaryButtonDisabled = () => {
    // Current Step Required Fields Have Errors
    // Current Step Required Fields Are Touched
    // Current Step Required Fields Are Not Empty

    return (
      (getKeys(errors).filter(
        (key) =>
          currentStepRequiredFieldsNames?.includes(key) ||
          (currentStepOptionalFieldsName.includes(key) &&
            !!getValue(values, key))
      ).length > 0 &&
        getKeys(touched).filter(
          (key) =>
            currentStepRequiredFieldsNames?.includes(key) ||
            currentStepOptionalFieldsName.includes(key)
        ).length > 0) ||
      !currentStepRequiredFieldsNames
        ?.map((name) => !!getValue(values, name))
        .every((value) => value) ||
      isSubmitting
    );
  };

  const secondaryButtonDisabled = () =>
    !(currentStepData?.step === 1 && currentStep?.disableButtonBack);

  // Update buttons when current step changes and when formik values change
  useEffect(() => {
    if (!currentStepData) return;

    if (!("component" in currentStepData)) {
      dispatch(
        onUpdatePrimaryButton({
          text: primaryButtonText[language],
          disabled: primaryButtonDisabled(),
          isLoading: false,
          type: "button",
          onClick: handleContinue,
          show: true,
        })
      );
      //Check if user came from the selector proyect
      const previousPageUrl = document.referrer?.includes(
        "seleccion-cuenta.allaria"
      );

      const onClickHandler =
        previousPageUrl && step === 1
          ? () => window.history.back()
          : handleBack;
      const showButton =
        currentStep?.step === 1 ? !!previousPageUrl : secondaryButtonDisabled();

      dispatch(
        onUpdateSecondaryButton({
          text: secondaryButtonText[language],
          disabled: false,
          isLoading: false,
          onClick: onClickHandler,
          show: showButton,
        })
      );

      dispatch(
        onUpdateTertiaryButton({
          text: tertiaryButtonText[language],
          disabled: false,
          isLoading: false,
          onClick: handleBack,
          show: false,
        })
      );
    }
  }, [
    currentStepData,
    values,
    errors,
    touched,
    isSubmitting,
    currentStep?.step,
  ]);

  // Reorder fields
  useEffect(() => {
    if (!currentStepData || !("fields" in currentStepData)) return;

    reorderFields(currentStepData, values, setValues);
  }, [values]);

  useEffect(() => {
    const handleBeforeUnload = () =>
      logAnalyticsAndHotjarEvent("onboarding_abandoned", { step });

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    // We iterate over all fields with errors
    Object.keys(errors).forEach((field) => {
      if (touched[field] && errors[field]) {
        // If the field has been touched and has an error, we trigger the event
        logAnalyticsAndHotjarEvent("form_error", {
          field,
          error: errors[field],
        });
      }
    });
  }, [errors, touched]);

  // Add referrer to form values if found in the URL
  useEffect(() => {
    if (template?.onboardingType === "SECURITIES") return;
    if (referrer) {
      const newValues = {
        referredBool: "SI",
        referrer: referrer.includes("@")
          ? referrer
          : referrer
              .split("-")
              .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
              .join(" "),
      };
      setValues(
        (prevValues) => ({
          ...prevValues,
          ...newValues,
        }),
        false
      );
      dispatch(onUpdateFormData(newValues));
    } else {
      // Fixing bug that keeped referrer in form values even when reloading the page
      // When no referrer is found in the URL, we reset the referrer field
      // This is done only when the user is in the first step, if not, the referrer is kept
      if (step === 1) {
        const valuesWithReferrerReseted = {
          referrer: "",
          referredBool: "",
        };

        setValues((prevValues) => ({
          ...prevValues,
          ...valuesWithReferrerReseted,
        }));
        dispatch(onUpdateFormData(valuesWithReferrerReseted));
      }
    }
  }, [referrer, loading.formValues, step]);

  // Add cuit and email to form values if found in the URL in unification onboardings
  useEffect(() => {
    return;
    if (!isDev) return; //TODO: Remove this before merge prod OB UNIFIED and fix the bug
    if (emailByUrl && cuitByUrl && isUnifiedOnboarding()) {
      let newValues;

      if (isJuridicalOnboarding(template.onboardingType)) {
        newValues = {
          email: emailByUrl,
          businessInformation: {
            ...(values.businessInformation || {}),
            cuit: cuitByUrl,
          },
        };
      } else {
        newValues = {
          email: emailByUrl,
          cuit: cuitByUrl,
        };
      }

      setValues(
        (prevValues) => ({
          ...prevValues,
          ...newValues,
        }),
        false
      );
      dispatch(onUpdateFormData(newValues));
    } else {
      // Fixing bug that keeped referrer in form values even when reloading the page
      // When no referrer is found in the URL, we reset the referrer field
      // This is done only when the user is in the first step, if not, the referrer is kept
      if (step === 1) {
        const valuesReseted = {
          cuit: "",
          email: "",
        };

        setValues((prevValues) => ({
          ...prevValues,
          ...valuesReseted,
        }));
        dispatch(onUpdateFormData(valuesReseted));
      }
    }
  }, [emailByUrl, cuitByUrl, loading.formValues, step]);

  // useEffect(() => {
  //   if (emailByUrl) {
  //     const newValues = {
  //       email: emailByUrl,
  //     };
  //     setValues(
  //       (prevValues) => ({
  //         ...prevValues,
  //         ...newValues,
  //       }),
  //       false
  //     );
  //     dispatch(onUpdateFormData(newValues));
  //   } else {
  //     // Fixing bug that keeped email in form values even when reloading the page
  //     // When no email is found in the URL, we reset the email field
  //     // This is done only when the user is in the first step, if not, the email is kept
  //     if (step === 1) {
  //       const valuesWithEmailReseted = {
  //         email: "",
  //       };

  //       setValues((prevValues) => ({
  //         ...prevValues,
  //         ...valuesWithEmailReseted,
  //       }));
  //       dispatch(onUpdateFormData(valuesWithEmailReseted));
  //     }
  //   }
  // }, [emailByUrl, loading.formValues, step]);
  const templateContainerStyle = () => {
    if (currentStepData?.supplementaryComponentInfo?.position === "top") {
      return `${templateContainer} ${flexColumn}`;
    }
    return `${templateContainer} `;
  };

  return (
    <Layout
      hideStepper={!!currentStep?.hideStepper}
      showLanguagePicker={!!currentStep?.showLanguagePicker}
    >
      {loading.template || loading.formValues ? (
        <div className={loadingStyle}>
          <LoadingCircle width="50" />
        </div>
      ) : (
        <>
          <EditDNIModal formik={formik} />
          <TramitNumberModal />
          <PEPModal />
          <RiskProfileModal />
          <AccountObjectiveModal language={language} />
          <RiskProfileModalSecurities language={language} />
          <div className={templateContainerStyle()}>
            {currentStepData?.supplementaryComponentInfo?.position ===
              "top" && (
              <SupplementaryComponentInfoRenderer
                supplementaryComponentInfo={
                  currentStepData?.supplementaryComponentInfo
                }
                state={state}
              />
            )}

            <Container
              className={` ${
                isComponentStep(currentStepData) &&
                currentStepData?.secondaryForm
                  ? form
                  : ""
              }`}
            >
              {isComponentStep(currentStepData) &&
              currentStepData?.component &&
              !currentStepData.insideForm ? (
                currentStepData.component(dispatch, state, formik)
              ) : (
                <FormContainer
                  icon={template?.icon}
                  currentStep={currentStep}
                  currentSubStep={
                    template.onboardingType === "SECURITIES"
                      ? Number(securities.stepData.subStep)
                      : Number(subStep)
                  }
                  className={`$${
                    !!isComponentStep(currentStepData) &&
                    currentStepData?.component
                      ? component
                      : ""
                  }`}
                  classNameFormContainer={`${
                    isComponentStep(currentStepData) &&
                    currentStepData?.secondaryForm
                      ? secondaryFormTemplate
                      : headerFormContainer
                  }`}
                >
                  {isComponentStep(currentStepData) &&
                    currentStepData?.component &&
                    currentStepData.component(dispatch, state, formik)}

                  {initialValues &&
                    isBasicStep(currentStepData) &&
                    currentStepData?.fields && (
                      <TemplateInterpreterForm
                        onSubmit={handleSubmit}
                        templateLoaded={!!template}
                        currentStepData={currentStepData}
                        formik={formik}
                      />
                    )}
                </FormContainer>
              )}
            </Container>
            {isComponentStep(currentStepData) &&
              currentStepData?.secondaryForm && (
                <TemplateInterpreterSecondaryForm
                  key={
                    (state.collectionData?.activeIndex ?? 0) +
                    "-" +
                    (state.onboardingData?.formValues?.shareholders?.[
                      (state.collectionData?.activeIndex ?? 1) - 1
                    ]?.personType || "")
                  }
                  {...currentStepData.secondaryForm}
                  primaryForm={formik}
                />
              )}
          </div>
          {currentStepData?.supplementaryComponentInfo?.position ===
            "bottom" && (
            <SupplementaryComponentInfoRenderer
              supplementaryComponentInfo={
                currentStepData?.supplementaryComponentInfo
              }
              state={state}
            />
          )}

          {!currentStepData?.hideLayoutButtons && (
            <div className={buttonsGroup}>
              {tertiaryButtonData && tertiaryButtonData.show && (
                <Button
                  variant="tertiary"
                  type={tertiaryButtonData?.type}
                  onClick={tertiaryButtonData?.onClick}
                  disabled={tertiaryButtonData?.disabled}
                  className={buttonTertiary}
                  text={tertiaryButtonData?.text || ""}
                  isLoading={tertiaryButtonData?.isLoading}
                />
              )}

              {secondaryButtonData && secondaryButtonData.show && (
                <Button
                  variant="secondary"
                  type={secondaryButtonData?.type}
                  onClick={secondaryButtonData?.onClick}
                  className={button}
                  disabled={secondaryButtonData?.disabled}
                  text={secondaryButtonData?.text || ""}
                  isLoading={secondaryButtonData?.isLoading}
                />
              )}

              {primaryButtonData && primaryButtonData.show && (
                <Button
                  variant="primary"
                  type={primaryButtonData?.type}
                  onClick={primaryButtonData?.onClick}
                  isLoading={primaryButtonData?.isLoading}
                  disabled={primaryButtonData?.disabled}
                  text={primaryButtonData?.text || ""}
                />
              )}
            </div>
          )}
        </>
      )}
    </Layout>
  );
};

export default TemplateInterpreter;
